<template>
  <div class="portal">
    <header ref="topbar" class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <img src="../../assets/logoK9corp.png" alt="Corporación K-9" class="brand-logo" />
          <div class="brand-text">
            <div class="brand-name">Portal del Candidato</div>
            <div class="brand-sub">Corporación K-9 · Reclutamiento</div>
          </div>
        </div>
        <div class="account">
          <span class="account-email" :title="userEmail">{{ userEmail }}</span>
          <button type="button" class="logout-btn" @click="requestLogout">
            <PortalIcon name="logout" :size="15" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </header>

    <main class="content" :class="{ 'has-save-bar': saveBarOpen }">
      <div v-if="loading" class="state-card">
        <span class="spinner" aria-hidden="true"></span>
        <span>Cargando su expediente…</span>
      </div>

      <div v-else-if="loadError" class="state-card">
        <span class="state-icon"><PortalIcon name="alert" :size="22" /></span>
        <div class="state-title">No pudimos cargar su expediente</div>
        <p class="state-text">Revise su conexión e intente de nuevo.</p>
        <button type="button" class="solid-btn" @click="loadCandidate()">Reintentar</button>
      </div>

      <div v-else class="layout">
        <PortalCredential
          class="area-credential"
          :candidate="credentialCandidate"
          :avatar-url="credentialAvatarUrl"
          :registry-name="registryName"
          :photo-editable="photoEditable"
          :photo-uploading="avatarUploading"
          @change-photo="changePhoto"
        />

        <div class="area-side">
          <PortalSectionNav
            class="area-steps"
            :candidate="candidate"
            :alerts-count="alerts.length"
            :active="tab"
            @select="selectSection($event, { scroll: true })"
          />
          <PortalStatusCard :candidate="candidate" @edit="openEditor" />
          <PortalAlertsCard v-if="alerts.length" :alerts="alerts" @edit="openEditor" />
        </div>

        <section ref="mainCard" class="area-main main-card">
          <div :id="'panel-' + tab" class="tab-panel" role="tabpanel" :aria-labelledby="'tab-' + tab">
            <PortalPersonalTab
              v-if="tab === 'personal'"
              :key="'personal-' + inlineKey"
              ref="sectionTab"
              :candidate="candidate"
              :user-email="userEmail"
              @dirty="dirtySection = $event"
              @live="live = $event"
              @avatar-uploaded="onAvatarUploaded"
              @email-changed="$emit('email-changed', $event)"
              @edit="openEditor"
              @saved="onSectionSaved"
              @autosaved="onAutosaved"
              @autosave-blocked="onAutosaveBlocked"
              @autosave-failed="(message) => showToast(message, true)"
            />
            <PortalDocumentsTab
              v-else-if="tab === 'documents'"
              :key="'documents-' + inlineKey"
              ref="sectionTab"
              :candidate="candidate"
              :user-email="userEmail"
              @dirty="dirtySection = $event"
              @edit="openEditor"
              @saved="onSectionSaved"
              @autosaved="onAutosaved"
              @autosave-blocked="onAutosaveBlocked"
              @autosave-failed="(message) => showToast(message, true)"
            />
            <PortalResidenceTab
              v-else
              :key="'residence-' + inlineKey"
              ref="sectionTab"
              :candidate="candidate"
              :user-email="userEmail"
              @dirty="dirtySection = $event"
              @edit="openEditor"
              @saved="onSectionSaved"
              @autosaved="onAutosaved"
              @autosave-blocked="onAutosaveBlocked"
              @autosave-failed="(message) => showToast(message, true)"
            />
          </div>
        </section>
      </div>
    </main>

    <DocumentViewer
      v-if="viewer"
      :title="viewer.title"
      :files="viewer.files"
      :initial-index="viewer.index"
      @close="viewer = null"
    />

    <DeclarationModal
      v-if="declarationOpen"
      :candidate="candidate"
      :user-email="userEmail"
      :from-residence="declarationFromResidence"
      @close="declarationOpen = false"
      @saved="onDeclarationSaved"
    />

    <ConfirmDialog
      v-if="pendingAction"
      destructive
      title="Tiene cambios sin guardar"
      message="Si continúa, perderá los cambios que hizo en esta sección."
      confirm-label="Descartar cambios"
      cancel-label="Seguir editando"
      @confirm="discardAndContinue()"
      @cancel="pendingAction = null"
    />

    <div class="toast-region" :class="{ 'above-bar': saveBarOpen }" role="status" aria-live="polite">
      <transition name="toast">
        <div v-if="toast.message" class="portal-toast" :class="{ 'is-error': toast.error }">
          <PortalIcon :name="toast.error ? 'alert' : 'check'" :size="16" />
          <span>{{ toast.message }}</span>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import * as candidateApi from "../../services/candidateApi";
import ConfirmDialog from "../ConfirmDialog.vue";
import DeclarationModal from "./DeclarationModal.vue";
import DocumentViewer from "./DocumentViewer.vue";
import PortalCredential from "./PortalCredential.vue";
import PortalAlertsCard from "./PortalAlertsCard.vue";
import PortalDocumentsTab from "./PortalDocumentsTab.vue";
import PortalIcon from "./PortalIcon.vue";
import PortalPersonalTab from "./PortalPersonalTab.vue";
import PortalResidenceTab from "./PortalResidenceTab.vue";
import PortalSectionNav from "./PortalSectionNav.vue";
import PortalStatusCard from "./PortalStatusCard.vue";
import { SECTIONS, documentAlerts, documentRows, sectionState } from "./candidateSummary";

// Where every logged-in applicant lands, new account or not (see App.vue): their whole application at a glance, with
// each wizard step completed or edited in a modal (a side panel on phones) that reuses that step's fields.
export default {
  name: "CandidatePortal",
  components: {
    ConfirmDialog,
    DeclarationModal,
    DocumentViewer,
    PortalAlertsCard,
    PortalCredential,
    PortalDocumentsTab,
    PortalIcon,
    PortalPersonalTab,
    PortalResidenceTab,
    PortalSectionNav,
    PortalStatusCard,
  },
  props: {
    userEmail: { type: String, default: "" },
  },
  // Lets any "Ver" button (FileLink) open the in-page document viewer.
  provide() {
    return {
      viewDocument: (doc) => {
        this.viewer = doc;
      },
    };
  },
  data() {
    return {
      loading: true,
      loadError: false,
      // The /candidate/me response ({} while the account has no application yet).
      candidate: null,
      // `tab` key of the open section (see SECTIONS).
      tab: "personal",
      // Document open in the viewer ({ title, files, index }), or null.
      viewer: null,
      // Temporary signed link for the stored profile photo (see getFileUrl()).
      avatarUrl: null,
      // A new profile photo (chosen with the credential's pencil) is uploading.
      avatarUploading: false,
      // The cédula física's name in the registry, shown until the applicant saves their own (see loadRegistryName()).
      registryName: null,
      toast: { message: "", error: false },
      toastTimer: null,
      // The open inline section has changes that aren't saved yet.
      dirtySection: false,
      // Datos personales as it stands while it is edited (photo, blood type, phone), shown on the credential before
      // it is saved; null when that section isn't open.
      live: null,
      // What the applicant asked for while dirtySection (leave the section, open an editor, log out), waiting on
      // whether to discard those changes.
      pendingAction: null,
      // Bumped to start an inline section over (its unsaved changes discarded).
      inlineKey: 0,
      // The declaration, the last step, is signed in a modal (see DeclarationModal): open, and whether it came right
      // after saving Residencia.
      declarationOpen: false,
      declarationFromResidence: false,
    };
  },
  computed: {
    documents() {
      return this.candidate ? documentRows(this.candidate) : [];
    },
    alerts() {
      return documentAlerts(this.documents);
    },
    // The credential: what is saved, with what Datos personales has right now over it.
    credentialCandidate() {
      if (!this.live || !this.candidate) return this.candidate;
      const { blood_type_id: bloodType, whatsapp_phone: whatsapp, cell_phone: cell } = this.live;
      return { ...this.candidate, blood_type_id: bloodType, whatsapp_phone: whatsapp, cell_phone: cell };
    },
    credentialAvatarUrl() {
      if (!this.live || !this.candidate) return this.avatarUrl;
      // The saved photo is already signed; another (or none) comes from the form once it has its own link.
      if ((this.live.image_url || null) === (this.candidate.image_url || null)) return this.avatarUrl;
      return this.live.image_url ? this.live.avatar_url || null : null;
    },
    // The credential's pencil: there is a photo, and Datos personales (the form that holds it) is the open section.
    photoEditable() {
      return !!this.live && !!this.live.image_url;
    },
    // Whether the open section shows its save bar (only while it still has to be completed: once completed, each
    // change is saved by itself): the notices then sit above it instead of over it.
    saveBarOpen() {
      if (!this.candidate) return false;
      const section = SECTIONS.find((item) => item.tab === this.tab);
      return !!section && sectionState(section.step, this.candidate) === "current";
    },
  },
  watch: {
    // Leaving a section drops what it was showing on the credential before saving: that is what is saved again.
    tab() {
      this.live = null;
    },
  },
  mounted() {
    this.loadCandidate();
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  },
  methods: {
    // The pencil on the credential's photo: the open Datos personales form takes the new one (uploads it and treats it
    // like a photo chosen there).
    changePhoto(file) {
      const section = this.$refs.sectionTab;
      if (!section || !section.changeAvatar || this.avatarUploading) return;
      this.avatarUploading = true;
      Promise.resolve(section.changeAvatar(file))
        .then((uploaded) => this.onAvatarUploaded(uploaded))
        .finally(() => {
          this.avatarUploading = false;
        });
    },
    // The profile photo went up (from the form's drop card or the credential's pencil), or it didn't.
    onAvatarUploaded(uploaded) {
      if (uploaded) this.showToast("Foto de perfil cargada con éxito.");
      else this.showToast("No se pudo subir la foto. Intente de nuevo.", true);
    },
    fetchCandidate() {
      return candidateApi
        .getMyCandidateDraft()
        .then((response) => response.data || {})
        .catch((error) => {
          // No candidate linked to this account yet: same as an application with nothing filled in.
          if (error.response && error.response.status === 404) return {};
          throw error;
        })
        .then((data) => {
          this.candidate = data;
          this.refreshSignedUrls();
          this.loadRegistryName();
        });
    },
    loadCandidate() {
      this.loading = true;
      this.loadError = false;
      this.fetchCandidate()
        .catch(() => {
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    signedUrl(prefix) {
      if (!prefix) return Promise.resolve(null);
      return candidateApi
        .getFileUrl(prefix)
        .then((response) => response.data || null)
        .catch(() => null);
    },
    refreshSignedUrls() {
      const imageKey = this.candidate.image_url;
      // Only applies if the candidate still has that same photo by the time it resolves.
      this.signedUrl(imageKey).then((url) => {
        if (this.candidate.image_url === imageKey) this.avatarUrl = url;
      });
    },
    // A new account only has its identification until "Datos personales" is saved; for a cédula física the
    // registry already knows the name (same lookup as the wizard's "Buscar").
    loadRegistryName() {
      const { gov_id: govId, identification_type_id: typeId, first_name: saved } = this.candidate;
      if (saved || !govId || String(typeId) !== "1") return;
      if (this.registryName && this.registryName.gov_id === govId) return;
      candidateApi
        .getPerson(govId)
        .then((response) => {
          const person = response.data;
          if (person && person.first_name && this.candidate.gov_id === govId) this.registryName = { ...person, gov_id: govId };
        })
        .catch(() => {}); // Not in the registry: the card shows "—" until the applicant enters their name.
    },
    // Runs `action` right away, or once the open section's changes are saved (autosave), or once the applicant agrees to
    // throw away the ones that can't be (a section still to be completed, or a change that isn't savable as it is).
    guardUnsaved(action) {
      if (!this.dirtySection) {
        action();
        return;
      }
      const section = this.$refs.sectionTab;
      const saved = section ? section.flush() : Promise.resolve(false);
      saved.then((clean) => {
        if (clean) action();
        else this.pendingAction = action;
      });
    },
    discardAndContinue() {
      const action = this.pendingAction;
      this.pendingAction = null;
      this.dirtySection = false;
      this.inlineKey += 1;
      action();
    },
    selectSection(tab, { scroll = false } = {}) {
      if (tab === this.tab) {
        if (scroll) this.scrollToSections();
        return;
      }
      this.guardUnsaved(() => {
        this.tab = tab;
        if (scroll) this.scrollToSections();
      });
    },
    // Brings the main card (the open section) to the top of the screen, right under the fixed top bar. On a phone it
    // sits below the credential, the status and the section cards; beside them on a wide screen, where it is already
    // in view.
    scrollToSections() {
      this.$nextTick(() => {
        const card = this.$refs.mainCard;
        if (!card) return;
        const bar = this.$refs.topbar ? this.$refs.topbar.offsetHeight : 0;
        const top = card.getBoundingClientRect().top;
        if (top >= bar && top <= bar + 120) return;
        const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top: window.scrollY + top - bar - 16, behavior: reduceMotion ? "auto" : "smooth" });
      });
    },
    requestLogout() {
      this.guardUnsaved(() => this.$emit("logout"));
    },
    openEditor(step) {
      if (!this.candidate) return;
      const section = SECTIONS[step];
      const state = sectionState(step, this.candidate);
      if (section.modal) {
        // The declaration opens in its modal, and only while it is still to be signed.
        if (state === "current") this.guardUnsaved(() => this.openDeclaration());
        return;
      }
      if (state === "locked") return;
      // The other sections are completed in their own tab: go there, and scroll down to it.
      this.selectSection(section.tab, { scroll: true });
    },
    openDeclaration({ fromResidence = false } = {}) {
      this.declarationFromResidence = fromResidence;
      this.declarationOpen = true;
    },
    // A section with a tab was saved (the declaration has its own: see onDeclarationSaved()).
    onSectionSaved(step) {
      const wasState = sectionState(step, this.candidate);
      const title = SECTIONS[step].title;
      this.fetchCandidate()
        .then(() => {
          if (wasState === "current") {
            // Completed. The editor that was just saved goes away with the tab, so there is nothing left to discard.
            const next = SECTIONS[step + 1];
            this.dirtySection = false;
            if (next.modal) {
              // The last section with a card: the declaration comes next, in its modal.
              this.openDeclaration({ fromResidence: true });
            } else {
              // Say so, and take the applicant to the next section, from its top.
              this.showToast(`Se guardó «${title}». Ahora complete la siguiente sección: ${next.title}.`, false, 7000);
              this.selectSection(next.tab, { scroll: true });
            }
          } else {
            this.showToast(`Cambios guardados: ${title}.`);
          }
        })
        .catch(() => {
          this.showToast("Se guardó, pero no pudimos actualizar esta página. Recárguela para ver los cambios.", true);
        });
    },
    // A change in a completed section saved by itself: the page shows what was saved, without starting the section over.
    onAutosaved({ candidate, labels }) {
      this.candidate = candidate;
      this.refreshSignedUrls();
      this.showToast(labels.length === 1 ? `Se guardó: ${labels[0]}.` : `Se guardaron los cambios: ${labels.join(", ")}.`);
    },
    // A change that can't be saved until the section is complete again (the fields are marked).
    onAutosaveBlocked(missing) {
      const shown = missing.slice(0, 3).join(", ");
      const more = missing.length > 3 ? ` y ${missing.length - 3} más` : "";
      this.showToast(`Para guardar sus cambios, complete: ${shown}${more}.`, false, 6000);
    },
    // The declaration was signed and the application sent: the four steps are done.
    onDeclarationSaved() {
      this.declarationOpen = false;
      this.fetchCandidate()
        .then(() => {
          this.showToast("¡Completó los 4 pasos! Su solicitud fue enviada y Reclutamiento ya puede revisar su expediente.", false, 8000);
          // Done: back to the first section (it was left on Residencia, the last one with a tab).
          this.selectSection(SECTIONS[0].tab, { scroll: true });
        })
        .catch(() => {
          this.showToast("Se guardó, pero no pudimos actualizar esta página. Recárguela para ver los cambios.", true);
        });
    },
    showToast(message, error = false, duration = error ? 7000 : 4000) {
      this.toast = { message, error };
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = { message: "", error: false };
      }, duration);
    },
  },
};
</script>

<style scoped>
.portal {
  --gutter: 32px;
  /* The layout's side column and the gap before the main one: the section's save bar lines up with the main card. */
  --side-width: 340px;
  --layout-gap: 24px;
  min-height: 100vh;
  /* The dark band behind the header is a fixed-height strip at the top of the
     page (a soft glow in its upper right); the cards overlap its lower edge. */
  background:
    radial-gradient(1200px 400px at 80% -10%, #1c2a4a 0%, transparent 60%) top / 100% 230px no-repeat,
    linear-gradient(#0a1020, #0a1020) top / 100% 230px no-repeat,
    #f4f5f8;
}

/* ---- top bar ---- */
/* Fixed at the top while the page scrolls under it. Opaque, painted like the dark strip the page has behind it (same
   glow in the upper right), so nothing shows through and it looks the same as it did before at the top. */
.topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background:
    radial-gradient(1200px 400px at 80% -23px, #1c2a4a 0%, transparent 60%),
    #0a1020;
}
.topbar-inner,
.content {
  max-width: 1600px;
  margin: 0 auto;
  padding-left: var(--gutter);
  padding-right: var(--gutter);
  /* Set here and not on .portal: the editor modal hosts the wizard form, which keeps its own type. */
  color: #0e1424;
  font-family: "Geist", system-ui, sans-serif;
  line-height: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.topbar-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 20px;
  padding-bottom: 21px;
}
/* The hairline under the header runs along the content, not edge to edge (it takes the last pixel of the padding). */
.topbar-inner::after {
  content: "";
  position: absolute;
  left: var(--gutter);
  right: var(--gutter);
  bottom: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
}
.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.brand-logo {
  flex-shrink: 0;
  display: block;
  width: 44px;
  height: 42px;
  object-fit: contain;
}
.brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.brand-name {
  font: 600 17px "Geist", system-ui, sans-serif;
  letter-spacing: -0.01em;
  color: #fff;
}
.brand-sub {
  font: 400 13px "Geist", system-ui, sans-serif;
  color: #8c96ad;
}
.account {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.account-email {
  max-width: 280px;
  height: 40px;
  padding: 0 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  font: 400 14px/40px "Geist", system-ui, sans-serif;
  color: #c9d0df;
}
.logout-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  font: 600 14px "Geist", system-ui, sans-serif;
  color: #fff;
  cursor: pointer;
}
.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}
.logout-btn svg {
  width: 16px;
  height: 16px;
}

/* ---- content: the cards sit over the lower edge of the dark band ---- */
.content {
  position: relative;
  padding-top: 32px;
  padding-bottom: 64px;
}
/* The save bar is pinned over the bottom of the screen: room under the main card so its lower edge shows above it. */
.content.has-save-bar {
  padding-bottom: 120px;
}
.layout {
  display: grid;
  grid-template-columns: var(--side-width) minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  grid-template-areas:
    "credential main"
    "side main";
  gap: 16px var(--layout-gap);
  align-items: start;
}
.area-credential {
  grid-area: credential;
}
.area-side {
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.area-main {
  grid-area: main;
}

.state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 56px 24px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.05), 0 8px 24px -10px rgba(10, 16, 32, 0.14);
  text-align: center;
  font: 400 14px "Geist", system-ui, sans-serif;
  color: #6b7283;
}
.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #eceef3;
  border-top-color: #e6b65c;
  border-radius: 50%;
  animation: portal-spin 0.8s linear infinite;
}
@keyframes portal-spin {
  to {
    transform: rotate(360deg);
  }
}
.state-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdecec;
  color: #c42b2b;
}
.state-title {
  font: 600 16px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.state-text {
  margin: 0 0 8px;
}
.solid-btn {
  height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 12px;
  background: #0e1424;
  box-shadow: 0 8px 18px -6px rgba(10, 16, 32, 0.6);
  color: #fff;
  font: 600 14px "Geist", system-ui, sans-serif;
  cursor: pointer;
}
.solid-btn:hover {
  background: #24304d;
}

/* ---- main card: the open section ---- */
.main-card {
  min-width: 0;
  /* Clips the open section to the card's rounded corners. */
  overflow: hidden;
  overflow: clip;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.06), 0 24px 48px -16px rgba(10, 16, 32, 0.22);
}
.tab-panel {
  padding: 28px;
}

/* ---- toast ---- */
.toast-region {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 24px;
  z-index: 950;
  display: flex;
  justify-content: center;
  padding: 0 16px;
  pointer-events: none;
}
.portal-toast {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 560px;
  padding: 12px 18px;
  border-radius: 12px;
  background: #0e1424;
  box-shadow: 0 14px 30px -12px rgba(10, 16, 32, 0.6);
  font: 600 14px/1.4 "Geist", system-ui, sans-serif;
  color: #fff;
  pointer-events: auto;
}
.portal-toast.is-error {
  background: #c42b2b;
}
.toast-region.above-bar {
  bottom: 100px;
}
.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 960px) {
  /* One column: the section list goes last in the side, right above the open section. */
  .area-steps {
    order: 1;
  }
  .layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    grid-template-areas:
      "credential"
      "side"
      "main";
  }
}

@media (max-width: 640px) {
  .portal {
    --gutter: 16px;
  }
  .brand-logo {
    width: 38px;
    height: 36px;
  }
  .brand-sub,
  .account-email {
    display: none;
  }
  .tab-panel {
    padding: 20px 16px 24px;
  }
  .content.has-save-bar {
    padding-bottom: 160px;
  }
  .toast-region.above-bar {
    bottom: 150px;
  }
}
</style>
